/**
 * 输出合成：选中线路保色、其余灰度（羽化边缘）—— 逐行对拍 climb-core/src/render.rs
 *
 * 口径契约：
 * - 岩点轮廓内部不做颜色校验统一保色；两类争议像素仍受颜色约束（Lab 色度 ≤20，低饱和放行）
 * - 抹除≠排除：抹除区域始终满强度抹平；排除仅灰度化（仍受保护不被净化抹掉）
 * - 描边高亮 accent 橙 #ff5a2c 与岩点颜色无关
 */
import { rgbToLab, labToRgb } from "./color";
import { dilate as morphDilate, blur as morphBlur } from "./morph";
import { buildFillField, wallRef } from "./wall";
import { resizeTriangle } from "./resize";
import { HoldKind, type Analysis, type Hold } from "./types";

/** accent 橙（GUI --accent #ff5a2c 一致） */
export const OUTLINE_RGB: [number, number, number] = [255, 90, 44];
/** 描边带宽/柔边（分析分辨率 px） */
export const OUTLINE_WIDTH = 2;
export const OUTLINE_FEATHER = 1.0;
/** 填充边界：岩点并集外扩/羽化半径 */
export const FILL_DILATE = 3;
export const FILL_FEATHER = 2.0;

/**
 * 生成"保留颜色"掩码（分析分辨率）。对拍 render.rs:14-87
 * sel：选中颜色组索引；undefined = 全灰。excluded/erased 为岩点 id 集合。
 */
export function keepMask(
  a: Analysis,
  sel: number | undefined,
  excluded: ReadonlySet<number>,
  erased: ReadonlySet<number>,
  dilateR: number,
): Uint8Array {
  const n = a.aw * a.ah;
  const core = new Uint8Array(n);
  if (sel === undefined || sel >= a.clusters.length) return core;
  const cl = a.clusters[sel];
  const raw = a.img;
  const okPx = (i: number): boolean => {
    const s = a.sat[i];
    const v = a.val[i];
    switch (cl.kind) {
      case HoldKind.Colored:
        if (s >= 0.22 && v >= 0.1) {
          const lab = rgbToLab(raw[i * 4], raw[i * 4 + 1], raw[i * 4 + 2]);
          return Math.sqrt((lab[1] - cl.ab[0]) ** 2 + (lab[2] - cl.ab[1]) ** 2) <= 20.0;
        }
        return true; // 低饱和（镁粉/胶痕/暗部）随簇保留
      case HoldKind.Black:
        return v <= 0.45 || s < 0.22;
      case HoldKind.White:
        return v >= 0.55 || s < 0.22;
      case HoldKind.Gray:
        return true;
    }
  };
  const visible = (h: Hold) => !excluded.has(h.id) && !erased.has(h.id);

  // 引用 labels 的自动岩点（回退路径）
  const includeLabels = new Set<number>();
  for (const h of a.holds) {
    if (h.cluster === sel && visible(h) && h.label > 0) includeLabels.add(h.label);
  }
  for (let i = 0; i < a.labels.length; i++) {
    const l = a.labels[i];
    if (l > 0 && includeLabels.has(l)) core[i] = 255;
  }

  // 自包含像素：先统计每像素被多少可见岩点覆盖（重叠区判据）
  const claim = new Uint8Array(n);
  for (const h of a.holds) {
    if (h.label === 0 && visible(h)) {
      for (const i of h.ownPixels) {
        if (claim[i] < 255) claim[i]++;
      }
    }
  }
  // 独占像素无条件保色；重叠像素过颜色校验
  for (const h of a.holds) {
    if (h.cluster === sel && visible(h) && h.label === 0) {
      for (const i of h.ownPixels) {
        if (claim[i] <= 1 || okPx(i)) core[i] = 255;
      }
    }
  }

  if (dilateR === 0) return core;
  // 外扩边缘带逐像素颜色约束（仅环带）
  const dil = morphDilate(core, a.aw, a.ah, dilateR);
  const mask = core;
  for (let i = 0; i < n; i++) {
    if (mask[i] === 0 && dil[i] > 0 && okPx(i)) mask[i] = 255;
  }
  return mask;
}

/** 掩码羽化（柔和边缘） */
export function feather(a: Analysis, mask: Uint8Array, radius: number): Uint8Array {
  if (radius <= 0) return mask.slice();
  return morphBlur(mask, a.aw, a.ah, radius);
}

/** 攀岩者掩码并入保色掩码（person_keep>0 时）。羽化前调用 */
export function keepPerson(mask: Uint8Array, a: Analysis, personKeep: number): void {
  if (personKeep > 0 && a.person) {
    for (const i of a.person) mask[i] = 255;
  }
}

/** 羽化后选中掩码 → 外向轮廓环带 alpha（阈值化→dilate−原掩码→柔边）。对拍 render.rs:121 */
export function outlineBand(a: Analysis, mask: Uint8Array): Uint8Array {
  const w = a.aw;
  const h = a.ah;
  const bin = Uint8Array.from(mask, (m) => (m > 128 ? 255 : 0));
  const dil = morphDilate(bin, w, h, OUTLINE_WIDTH);
  const band = new Uint8Array(w * h);
  for (let i = 0; i < band.length; i++) {
    band[i] = dil[i] - bin[i]; // saturating_sub：两者同域 0/255
  }
  return feather(a, band, OUTLINE_FEATHER);
}

/** 分析分辨率描边带放大到全分辨率（image-rs Triangle） */
export function outlineBandFull(a: Analysis, mask: Uint8Array): Uint8Array {
  const band = outlineBand(a, mask);
  return resizeTriangle(band, a.aw, a.ah, a.fw, a.fh, 1);
}

/** 描边染色：band_alpha 与 img 同尺寸；strength 0..1。直接改 img。对拍 render.rs:137 */
export function applyOutline(
  img: Uint8ClampedArray,
  bandAlpha: Uint8Array,
  strength: number,
): void {
  const k = Math.min(Math.max(strength, 0), 1);
  if (k <= 0) return;
  for (let i = 0; i < bandAlpha.length; i++) {
    const t = (bandAlpha[i] / 255) * k;
    if (t > 0) {
      const p = i * 4;
      img[p] = Math.trunc(img[p] + (OUTLINE_RGB[0] - img[p]) * t);
      img[p + 1] = Math.trunc(img[p + 1] + (OUTLINE_RGB[1] - img[p + 1]) * t);
      img[p + 2] = Math.trunc(img[p + 2] + (OUTLINE_RGB[2] - img[p + 2]) * t);
    }
  }
}

/** 填充层（分析分辨率）：每像素墙面参考色 + 填充 alpha（已含强度） */
export interface FillLayer {
  rgb: Uint8Array; // n*3
  alpha: Uint8Array;
}

/** 按谓词收集岩点并集掩码（label 引用与 ownPixels 两种表示都计入）。对拍 render.rs:168 */
function holdsMaskImpl(a: Analysis, include: (h: Hold) => boolean): Uint8Array {
  const n = a.aw * a.ah;
  const m = new Uint8Array(n);
  const includeLabels = new Set<number>();
  for (const h of a.holds) {
    if (h.label > 0 && include(h)) includeLabels.add(h.label);
  }
  if (includeLabels.size > 0) {
    for (let i = 0; i < a.labels.length; i++) {
      const l = a.labels[i];
      if (l > 0 && includeLabels.has(l)) m[i] = 255;
    }
  }
  for (const h of a.holds) {
    if (h.label === 0 && include(h)) {
      for (const i of h.ownPixels) m[i] = 255;
    }
  }
  return m;
}

/**
 * 构造墙面净化填充层（对拍 render.rs:204-245）。
 * bgFill<=0 且无抹除点 → null。抹除区域始终满强度抹平。
 */
export function fillLayer(
  a: Analysis,
  erased: ReadonlySet<number>,
  bgFill: number,
): FillLayer | null {
  const k = Math.min(Math.max(bgFill, 0), 1);
  if (k <= 0 && erased.size === 0) return null;
  const w = a.aw;
  const h = a.ah;
  const n = w * h;
  const union = holdsMaskImpl(a, (hd) => !erased.has(hd.id));
  const exclude = morphDilate(union, w, h, FILL_DILATE);
  // 净化不抹人（项目所有者裁定，2026-10-22）：攀岩者像素并入排除区——
  // 既不被墙面色抹平，也不污染墙面场的亮部统计（上游 render.rs 无此步，
  // 属 web 端有意的体验分叉，对拍口径以本注释为准）
  if (a.person) {
    for (const i of a.person) exclude[i] = 255;
  }
  const field = buildFillField(a.img, a.sat, exclude, w, h);

  // 填充 alpha = 墙面掩码（并集外扩区补集）羽化 × 强度
  const wall = Uint8Array.from(exclude, (e) => (e > 0 ? 0 : 255));
  const feathered = feather(a, wall, FILL_FEATHER);
  const alpha = Uint8Array.from(feathered, (v) =>
    Math.round((v / 255) * k * 255),
  );

  // 抹除区域：外扩羽化后满强度，压过墙面强度
  if (erased.size > 0) {
    const em = holdsMaskImpl(a, (hd) => erased.has(hd.id));
    const emd = morphDilate(em, w, h, FILL_DILATE);
    const emf = feather(a, emd, FILL_FEATHER);
    for (let i = 0; i < n; i++) {
      if (emf[i] > alpha[i]) alpha[i] = emf[i];
    }
  }

  // 每像素填充色（墙面场双线性采样 → RGB）
  const rgb = new Uint8Array(n * 3);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const { lab } = wallRef(field, x, y);
      const [r, g, b] = labToRgb(lab[0], lab[1], lab[2]);
      const i = (y * w + x) * 3;
      rgb[i] = r;
      rgb[i + 1] = g;
      rgb[i + 2] = b;
    }
  }
  return { rgb, alpha };
}

function fillPx(img: Uint8ClampedArray, p: number, fc: Uint8Array, fi: number, t: number): void {
  img[p] = Math.trunc(img[p] + (fc[fi] - img[p]) * t);
  img[p + 1] = Math.trunc(img[p + 1] + (fc[fi + 1] - img[p + 1]) * t);
  img[p + 2] = Math.trunc(img[p + 2] + (fc[fi + 2] - img[p + 2]) * t);
}

/** 保色/灰度混合（bg_sat>0 时背景保留一点原色）。对拍 render.rs:254-265 */
function blendPx(img: Uint8ClampedArray, p: number, a: number, bgSat: number): void {
  const r = img[p];
  const g = img[p + 1];
  const b = img[p + 2];
  const luma = 0.299 * r + 0.587 * g + 0.114 * b;
  const gr = luma + (r - luma) * bgSat;
  const gg = luma + (g - luma) * bgSat;
  const gb = luma + (b - luma) * bgSat;
  img[p] = Math.min(Math.max(Math.trunc(gr + (r - gr) * a), 0), 255);
  img[p + 1] = Math.min(Math.max(Math.trunc(gg + (g - gg) * a), 0), 255);
  img[p + 2] = Math.min(Math.max(Math.trunc(gb + (b - gb) * a), 0), 255);
}

/**
 * 全分辨率合成：mask（分析分辨率羽化）Triangle 放大到原图；
 * fill 存在时先净化底图再走保色/灰度。返回新图。对拍 composite_full
 */
export function compositeFull(
  full: Uint8ClampedArray,
  a: Analysis,
  mask: Uint8Array,
  bgSat: number,
  fill: FillLayer | null,
): Uint8ClampedArray {
  const alpha = resizeTriangle(mask, a.aw, a.ah, a.fw, a.fh, 1);
  const buf = Uint8ClampedArray.from(full);
  const n = a.fw * a.fh;
  if (fill) {
    const frgb = resizeTriangle(fill.rgb, a.aw, a.ah, a.fw, a.fh, 3);
    const fa = resizeTriangle(fill.alpha, a.aw, a.ah, a.fw, a.fh, 1);
    for (let i = 0; i < n; i++) {
      const p = i * 4;
      fillPx(buf, p, frgb, i * 3, fa[i] / 255);
      blendPx(buf, p, alpha[i] / 255, bgSat);
    }
  } else {
    for (let i = 0; i < n; i++) {
      blendPx(buf, i * 4, alpha[i] / 255, bgSat);
    }
  }
  return buf;
}

/** 分析分辨率预览（GUI 用）；fill 语义同 compositeFull。对拍 composite_preview */
export function compositePreview(
  a: Analysis,
  mask: Uint8Array,
  bgSat: number,
  fill: FillLayer | null,
): Uint8ClampedArray {
  const buf = Uint8ClampedArray.from(a.img);
  const n = a.aw * a.ah;
  if (fill) {
    for (let i = 0; i < n; i++) {
      const p = i * 4;
      fillPx(buf, p, fill.rgb, i * 3, fill.alpha[i] / 255);
      blendPx(buf, p, mask[i] / 255, bgSat);
    }
  } else {
    for (let i = 0; i < n; i++) {
      blendPx(buf, i * 4, mask[i] / 255, bgSat);
    }
  }
  return buf;
}
